import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  defaultStructuredJsonModel,
  emptyStructuredJsonItem,
  JsonArrayItem,
  mergeStructuredJsonModel,
  parseStructuredJsonArray,
  parseStructuredJsonPayload,
  serializeStructuredJsonArray,
  serializeStructuredJsonPayload,
  structuredJsonItemFromPayload
} from '../../shared/design-system/structured-json-form.utils';

export type { JsonArrayItem };
export {
  emptyStructuredJsonItem,
  parseStructuredJsonArray,
  serializeStructuredJsonArray,
  structuredJsonItemFromPayload
};

export const QUOTE_STATUS_OPTIONS = [
  { value: 'Draft', labelKey: 'insurance.quotes.status.draft' },
  { value: 'Priced', labelKey: 'insurance.quotes.status.priced' },
  { value: 'Expired', labelKey: 'insurance.quotes.status.expired' },
  { value: 'ConvertedToProposal', labelKey: 'insurance.quotes.status.convertedToProposal' },
  { value: 'Cancelled', labelKey: 'insurance.quotes.status.cancelled' }
] as const;

export const QUOTE_RISK_OBJECT_ITEM_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'objectType',
    type: 'select',
    labelKey: 'insurance.quoteDetail.schemas.riskObject.objectType',
    options: [
      { value: 'Vehicle', labelKey: 'insurance.quoteDetail.schemas.riskObject.types.vehicle' },
      { value: 'Property', labelKey: 'insurance.quoteDetail.schemas.riskObject.types.property' },
      { value: 'Person', labelKey: 'insurance.quoteDetail.schemas.riskObject.types.person' },
      { value: 'Other', labelKey: 'insurance.quoteDetail.schemas.riskObject.types.other' }
    ]
  },
  { key: 'vehicleType', type: 'string', labelKey: 'insurance.quoteDetail.schemas.riskObject.vehicleType' },
  { key: 'makeModel', type: 'string', labelKey: 'insurance.quoteDetail.schemas.riskObject.makeModel' },
  { key: 'modelYear', type: 'number', labelKey: 'insurance.quoteDetail.schemas.riskObject.modelYear' },
  { key: 'vin', type: 'string', labelKey: 'insurance.quoteDetail.schemas.riskObject.vin' },
  { key: 'usage', type: 'string', labelKey: 'insurance.quoteDetail.schemas.riskObject.usage' },
  { key: 'zoneCode', type: 'string', labelKey: 'insurance.quoteDetail.schemas.zoneCode' },
  { key: 'description', type: 'textarea', labelKey: 'insurance.quoteDetail.schemas.description' },
  { key: 'identifier', type: 'string', labelKey: 'insurance.quoteDetail.schemas.riskObject.identifier' },
  { key: 'plate', type: 'string', labelKey: 'insurance.quoteDetail.schemas.riskObject.plate' },
  { key: 'territoryId', type: 'select', labelKey: 'insurance.quoteDetail.schemas.riskObject.territoryId' }
];

export const QUOTE_SELECTED_COVERAGE_ITEM_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'coverageCode',
    type: 'select',
    labelKey: 'insurance.quoteDetail.schemas.coverageCode',
    required: true
  },
  { key: 'sumInsured', type: 'number', labelKey: 'insurance.quoteDetail.schemas.sumInsured' },
  { key: 'selected', type: 'boolean', labelKey: 'insurance.quoteDetail.schemas.selected' }
];

export const QUOTE_PRICING_SNAPSHOT_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'mode', type: 'string', labelKey: 'insurance.quoteDetail.schemas.pricingMode' },
  { key: 'planCode', type: 'string', labelKey: 'insurance.quoteDetail.schemas.planCode' },
  { key: 'basePremium', type: 'number', labelKey: 'insurance.quoteDetail.schemas.basePremium' },
  { key: 'driverAccident', type: 'number', labelKey: 'insurance.quoteDetail.schemas.pricingSnapshot.driverAccident' },
  { key: 'extraFinancial', type: 'number', labelKey: 'insurance.quoteDetail.schemas.pricingSnapshot.extraFinancial' },
  { key: 'discount', type: 'number', labelKey: 'insurance.quoteDetail.schemas.pricingSnapshot.discount' },
  { key: 'penalty', type: 'number', labelKey: 'insurance.quoteDetail.schemas.pricingSnapshot.penalty' },
  { key: 'vat', type: 'number', labelKey: 'insurance.quoteDetail.schemas.pricingSnapshot.vat' },
  { key: 'total', type: 'number', labelKey: 'insurance.quoteDetail.schemas.pricingSnapshot.total' },
  { key: 'totalLoadings', type: 'number', labelKey: 'insurance.quoteDetail.schemas.totalLoadings' },
  { key: 'totalDiscounts', type: 'number', labelKey: 'insurance.quoteDetail.schemas.totalDiscounts' },
  { key: 'totalTaxesAndFees', type: 'number', labelKey: 'insurance.quoteDetail.schemas.totalTaxesAndFees' },
  { key: 'totalPremium', type: 'number', labelKey: 'insurance.quoteDetail.schemas.totalPremium' }
];

export const INSURANCE_QUOTE_RISK_OBJECT_FIELDS = QUOTE_RISK_OBJECT_ITEM_FIELDS;
export const INSURANCE_QUOTE_COVERAGE_FIELDS = QUOTE_SELECTED_COVERAGE_ITEM_FIELDS;

export function defaultItemModel(fields: StructuredJsonFieldDef[]): JsonArrayItem {
  return defaultStructuredJsonModel(fields);
}

export function mergeItemModel(fields: StructuredJsonFieldDef[], existing: JsonArrayItem): JsonArrayItem {
  return mergeStructuredJsonModel(fields, existing);
}

export function parseJsonArray(raw?: string | null): JsonArrayItem[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(item => item && typeof item === 'object' && !Array.isArray(item))
      .map(item => item as JsonArrayItem);
  } catch {
    return [];
  }
}

export function parseRiskObjectsJson(raw?: string | null): JsonArrayItem[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .filter(item => item && typeof item === 'object' && !Array.isArray(item))
        .map(item => mergeItemModel(QUOTE_RISK_OBJECT_ITEM_FIELDS, item as JsonArrayItem));
    }
    if (parsed && typeof parsed === 'object') {
      return [mergeItemModel(QUOTE_RISK_OBJECT_ITEM_FIELDS, parsed as JsonArrayItem)];
    }
  } catch {
    // fall through
  }

  return [];
}

export function parseSelectedCoveragesJson(raw?: string | null): JsonArrayItem[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map(item => {
      if (typeof item === 'string') {
        return mergeItemModel(QUOTE_SELECTED_COVERAGE_ITEM_FIELDS, {
          coverageCode: item,
          selected: true
        });
      }
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return mergeItemModel(QUOTE_SELECTED_COVERAGE_ITEM_FIELDS, item as JsonArrayItem);
      }
      return defaultItemModel(QUOTE_SELECTED_COVERAGE_ITEM_FIELDS);
    });
  } catch {
    return [];
  }
}

export function serializeJsonArray(items: JsonArrayItem[]): string {
  const cleaned = items
    .map(item => JSON.parse(serializeStructuredJsonPayload(item)) as JsonArrayItem)
    .filter(item => Object.keys(item).length > 0);
  return JSON.stringify(cleaned);
}

export function parseCoverageSelectionJson(json: string): JsonArrayItem[] {
  return parseSelectedCoveragesJson(json);
}

export function riskObjectPayloadJson(item: JsonArrayItem): string {
  return serializeStructuredJsonPayload(mergeItemModel(QUOTE_RISK_OBJECT_ITEM_FIELDS, item));
}

export function quoteStatusLabelKey(status: string): string {
  return `insurance.quotes.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
}

export function riskObjectSummary(row: JsonArrayItem): string {
  const description = typeof row['description'] === 'string' ? row['description'] : '';
  const makeModel = typeof row['makeModel'] === 'string' ? row['makeModel'] : '';
  const plate = typeof row['plate'] === 'string' ? row['plate'] : '';
  const zone = typeof row['zoneCode'] === 'string' ? row['zoneCode'] : '';
  return description || makeModel || plate || zone || '-';
}

export function coverageSummary(row: JsonArrayItem): string {
  const code = typeof row['coverageCode'] === 'string' ? row['coverageCode'] : '-';
  const sumInsured = row['sumInsured'];
  const selected = row['selected'] === true || row['selected'] === 'true';
  const sumText = sumInsured === null || sumInsured === undefined || sumInsured === '' ? '-' : `${sumInsured}`;
  return `${code} (${sumText}${selected ? ', ✓' : ''})`;
}

export function formatBooleanDisplay(value: unknown, yesLabel: string, noLabel: string): string {
  if (value === true || value === 'true') {
    return yesLabel;
  }
  if (value === false || value === 'false') {
    return noLabel;
  }
  return '-';
}
